import React, { useState, useEffect, useMemo } from 'react';
import { edefterFetch } from '../../services/edefterApi';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Avatar,
  Paper,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  FormLabel,
  Button,
  Divider,
  Collapse,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  AttachMoney,
  CalendarToday,
  Business,
  Description,
  Download,
  Email,
  Phone,
  LocationOn,
  AccountBalance,
  ExpandMore,
  ChevronRight,
  Upload,
  PictureAsPdf,
} from '@mui/icons-material';

const EDEFTER_API_URL = window.APP_CONFIG?.edefterApiUrl || 'https://srv.dpg.net.tr/edefter';

const TrialBalance = () => {
  const [identifiers, setIdentifiers] = useState([]);
  const [selectedIdentifier, setSelectedIdentifier] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [mizanData, setMizanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [error, setError] = useState(null);
  const [identifierInputValue, setIdentifierInputValue] = useState('');
  const [viewMode, setViewMode] = useState('single'); // 'single' or 'period'
  const [companyInfo, setCompanyInfo] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set()); // Açık olan account code'lar
  const [pdfData, setPdfData] = useState(null); // PDF'ten okunan veriler
  const [pdfFile, setPdfFile] = useState(null); // Yüklenen PDF dosyası (görüntüleme için)
  const [uploadingPdf, setUploadingPdf] = useState(false); // PDF yükleme durumu
  const [activeTab, setActiveTab] = useState(0); // Tab seçimi: 0 = Table, 1 = PDF
  
  // Dönem seçenekleri (kümülatif) - belgelerden yılı alarak dinamik oluşturuluyor
  const periods = useMemo(() => {
    // Belgelerden yılı çıkar
    let year = new Date().getFullYear(); // Varsayılan olarak mevcut yıl
    
    if (documents && documents.length > 0) {
      // İlk belgenin periodCoveredStart veya periodCoveredEnd'inden yılı al
      const firstDoc = documents[0];
      const dateStr = firstDoc.periodCoveredStart || firstDoc.periodCoveredEnd;
      if (dateStr) {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          year = date.getFullYear();
        }
      }
    }
    
    return [
      { value: '1', label: `1. Dönem (Ocak-Mart - 3 Ay)`, start: `${year}-01-01`, end: `${year}-03-31` },
      { value: '2', label: `2. Dönem (Ocak-Haziran - 6 Ay)`, start: `${year}-01-01`, end: `${year}-06-30` },
      { value: '3', label: `3. Dönem (Ocak-Eylül - 9 Ay)`, start: `${year}-01-01`, end: `${year}-09-30` },
      { value: '4', label: `4. Dönem (Ocak-Aralık - 12 Ay)`, start: `${year}-01-01`, end: `${year}-12-31` },
    ];
  }, [documents]);

  // Fetch all identifiers
  useEffect(() => {
    const fetchIdentifiers = async () => {
      try {
        setLoading(true);
        const response = await edefterFetch(`${EDEFTER_API_URL}/identifiers`);
        if (response.ok) {
          const data = await response.json();
          setIdentifiers(data || []);
        }
      } catch (err) {
        console.error('Error fetching identifiers:', err);
        setError('Failed to load identifiers');
      } finally {
        setLoading(false);
      }
    };

    fetchIdentifiers();
  }, []);

  // Fetch documents when identifier is selected
  useEffect(() => {
    if (!selectedIdentifier) {
      setDocuments([]);
      setSelectedDocument(null);
      setMizanData(null);
      setCompanyInfo(null);
      return;
    }

    const fetchDocuments = async () => {
      try {
        setLoadingDocuments(true);
        const response = await edefterFetch(`${EDEFTER_API_URL}/documents-by-identifier?identifier=${selectedIdentifier}`);
        if (response.ok) {
          const data = await response.json();
          setDocuments(data || []);
          
          // Identifier'dan direkt companyInfo'yu al
          try {
            const companyInfoResponse = await edefterFetch(`${EDEFTER_API_URL}/company-info-by-identifier?identifier=${selectedIdentifier}`);
            if (companyInfoResponse.ok) {
              const companyInfoData = await companyInfoResponse.json();
              console.log('Company info from identifier:', companyInfoData);
              if (companyInfoData) {
                setCompanyInfo(companyInfoData);
              }
            }
          } catch (err) {
            console.error('Error fetching company info:', err);
          }
        } else {
          setError('Failed to load documents');
        }
      } catch (err) {
        console.error('Error fetching documents:', err);
        setError('Failed to load documents');
      } finally {
        setLoadingDocuments(false);
      }
    };

    fetchDocuments();
  }, [selectedIdentifier]);

  // Fetch mizan when document is selected (single month view)
  useEffect(() => {
    if (viewMode !== 'single' || !selectedDocument) {
      if (viewMode === 'single') {
        setMizanData(null);
      }
      return;
    }

    const fetchMizan = async () => {
      try {
        setLoading(true);
        const response = await edefterFetch(`${EDEFTER_API_URL}/mizan-by-document?documentId=${selectedDocument.id}`);
        if (response.ok) {
          const data = await response.json();
          setMizanData(data);
        } else {
          setError('Failed to load mizan');
        }
      } catch (err) {
        console.error('Error fetching mizan:', err);
        setError('Failed to load mizan');
      } finally {
        setLoading(false);
      }
    };

    fetchMizan();
  }, [selectedDocument, viewMode]);

  // Fetch aggregated mizan when period is selected
  useEffect(() => {
    if (viewMode !== 'period' || !selectedIdentifier || !selectedPeriod) {
      if (viewMode === 'period') {
        setMizanData(null);
      }
      return;
    }

    const fetchPeriodMizan = async () => {
      try {
        setLoading(true);
        setError(null);
        const period = periods.find(p => p.value === selectedPeriod);
        if (!period) {
          setError('Period not found');
          return;
        }
        
        console.log('Fetching period mizan:', {
          identifier: selectedIdentifier,
          periodStart: period.start,
          periodEnd: period.end
        });
        
        const response = await edefterFetch(
          `${EDEFTER_API_URL}/mizan-by-period?identifier=${selectedIdentifier}&periodStart=${period.start}&periodEnd=${period.end}`
        );
        if (response.ok) {
          const data = await response.json();
          console.log('Period mizan data:', data);
          setMizanData(data);
          
          // Kaydedilmiş PDF'i yükle
          if (viewMode === 'period' && selectedIdentifier && selectedPeriod) {
            const period = periods.find(p => p.value === selectedPeriod);
            if (period) {
              try {
                const pdfResponse = await edefterFetch(
                  `${EDEFTER_API_URL}/getPdf?identifier=${encodeURIComponent(selectedIdentifier)}&periodStart=${encodeURIComponent(period.start)}&periodEnd=${encodeURIComponent(period.end)}`
                );
                if (pdfResponse.ok) {
                  const blob = await pdfResponse.blob();
                  const fileUrl = URL.createObjectURL(blob);
                  // Eski URL'i temizle
                  if (pdfFile) {
                    URL.revokeObjectURL(pdfFile);
                  }
                  setPdfFile(fileUrl);
                } else {
                  // PDF bulunamadıysa, mevcut PDF'i temizle (yeni period için PDF yok)
                  if (pdfFile) {
                    URL.revokeObjectURL(pdfFile);
                  }
                  setPdfFile(null);
                }
              } catch (pdfErr) {
                console.error('Error loading saved PDF:', pdfErr);
                // Hata durumunda mevcut PDF'i temizle
                if (pdfFile) {
                  URL.revokeObjectURL(pdfFile);
                }
                setPdfFile(null);
              }
            }
          }
        } else {
          const errorText = await response.text();
          console.error('Failed to load period mizan:', errorText);
          setError('Failed to load period mizan: ' + errorText);
        }
      } catch (err) {
        console.error('Error fetching period mizan:', err);
        setError('Failed to load period mizan: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPeriodMizan();
  }, [selectedPeriod, selectedIdentifier, viewMode, periods]);

  const handleIdentifierChange = (event, newValue, reason) => {
    if (reason === 'selectOption') {
      setSelectedIdentifier(newValue);
      setIdentifierInputValue(newValue);
      setSelectedDocument(null);
      setSelectedPeriod(null);
      setMizanData(null);
      setCompanyInfo(null);
      setError(null);
    } else if (reason === 'clear') {
      setSelectedIdentifier(null);
      setIdentifierInputValue('');
      setSelectedDocument(null);
      setSelectedPeriod(null);
      setDocuments([]);
      setMizanData(null);
      setCompanyInfo(null);
      setError(null);
    }
  };

  const handleDocumentChange = (event, newValue, reason) => {
    if (reason === 'selectOption') {
      setSelectedDocument(newValue);
      setSelectedPeriod(null);
      setViewMode('single');
      setError(null);
    } else if (reason === 'clear') {
      setSelectedDocument(null);
      setMizanData(null);
      setError(null);
    }
  };

  const handlePeriodChange = (event, newValue, reason) => {
    if (reason === 'selectOption' && newValue) {
      // Period değiştiğinde eski verileri temizle
      setMizanData(null);
      setPdfData(null);
      if (pdfFile) {
        URL.revokeObjectURL(pdfFile);
      }
      setPdfFile(null);
      setSelectedPeriod(newValue.value);
      setSelectedDocument(null);
      setViewMode('period');
      setError(null);
    } else if (reason === 'clear') {
      setSelectedPeriod(null);
      setMizanData(null);
      setPdfData(null);
      if (pdfFile) {
        URL.revokeObjectURL(pdfFile);
      }
      setPdfFile(null);
      setError(null);
    }
  };

  const handleViewModeChange = (event) => {
    const mode = event.target.value;
    setViewMode(mode);
    if (mode === 'single') {
      setSelectedPeriod(null);
    } else {
      setSelectedDocument(null);
    }
    setMizanData(null);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0,00';
    const number = parseFloat(value);
    if (isNaN(number)) return '0,00';
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const handleDownloadExcel = async () => {
    if (!mizanData || !selectedIdentifier) return;

    try {
      setLoading(true);
      let url;
      
      if (viewMode === 'period' && selectedPeriod) {
        // Kümülatif dönem için
        const period = periods.find(p => p.value === selectedPeriod);
        if (!period) return;
        
        url = `${EDEFTER_API_URL}/mizan-by-period-excel?identifier=${selectedIdentifier}&periodStart=${period.start}&periodEnd=${period.end}`;
      } else if (viewMode === 'single' && selectedDocument) {
        // Tek ay için
        url = `${EDEFTER_API_URL}/mizan-by-document-excel?documentId=${selectedDocument.id}`;
      } else {
        return;
      }

      const response = await edefterFetch(url);
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        
        // Dosya adını oluştur
        let filename = 'mizan';
        if (viewMode === 'period' && selectedPeriod) {
          const period = periods.find(p => p.value === selectedPeriod);
          filename = `mizan_${selectedIdentifier}_${period.start}_${period.end}`;
        } else if (viewMode === 'single' && selectedDocument) {
          filename = `mizan_${selectedIdentifier}_${selectedDocument.periodCoveredStart}_${selectedDocument.periodCoveredEnd}`;
        }
        a.download = `${filename}.xlsx`;
        
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        const errorText = await response.text();
        setError('Failed to download Excel: ' + errorText);
      }
    } catch (err) {
      console.error('Error downloading Excel:', err);
      setError('Failed to download Excel: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getDocumentLabel = (doc) => {
    if (!doc) return '';
    const start = formatDate(doc.periodCoveredStart);
    const end = formatDate(doc.periodCoveredEnd);
    return `${start} - ${end} (${doc.uniqueId || doc.id || ''})`;
  };

  
  const getDocumentKey = (doc) => {
    if (!doc) return '';
    // Use id as primary key, fallback to uniqueId + period if id not available
    return doc.id ? `doc-${doc.id}` : `doc-${doc.uniqueId}-${doc.periodCoveredStart}-${doc.periodCoveredEnd}`;
  };

  // Mizan verilerini account code'a göre grupla (hem single hem period modunda)
  const groupedMizanData = useMemo(() => {
    if (!mizanData || !mizanData.rows) return null;

    const groups = new Map();

    mizanData.rows.forEach((row) => {
      const accountCode = row.hesapKodu || '';
      if (!groups.has(accountCode)) {
        groups.set(accountCode, {
          accountCode,
          mainDescription: row.mainDescription || '',
          baseRow: null,
          subRows: [],
        });
      }

      const group = groups.get(accountCode);
      if (!group.mainDescription && row.mainDescription) {
        group.mainDescription = row.mainDescription;
      }

      if (row.subId) {
        group.subRows.push(row);
      } else {
        group.baseRow = row;
      }
    });

    return Array.from(groups.values())
      .map((group) => {
        const totalsFromSubRows = group.subRows.reduce(
          (acc, row) => {
            acc.borc += row.borc || 0;
            acc.alacak += row.alacak || 0;
            acc.borcBakiyesi += row.borcBakiyesi || 0;
            acc.alacakBakiyesi += row.alacakBakiyesi || 0;
            return acc;
          },
          { borc: 0, alacak: 0, borcBakiyesi: 0, alacakBakiyesi: 0 }
        );

        const hasMultipleDetails = group.subRows.length > 1;
        const hasAnyDetail = group.subRows.length > 0;

        return {
          ...group,
          totalBorc: hasAnyDetail ? totalsFromSubRows.borc : group.baseRow?.borc || 0,
          totalAlacak: hasAnyDetail ? totalsFromSubRows.alacak : group.baseRow?.alacak || 0,
          totalBorcBakiyesi: hasAnyDetail ? totalsFromSubRows.borcBakiyesi : group.baseRow?.borcBakiyesi || 0,
          totalAlacakBakiyesi: hasAnyDetail ? totalsFromSubRows.alacakBakiyesi : group.baseRow?.alacakBakiyesi || 0,
          hasMultipleDetails,
        };
      })
      .sort((a, b) => {
        return (a.accountCode || '').localeCompare(b.accountCode || '');
      });
  }, [mizanData, viewMode]);

  const handleToggleRow = (accountCode) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(accountCode)) {
      newExpanded.delete(accountCode);
    } else {
      newExpanded.add(accountCode);
    }
    setExpandedRows(newExpanded);
  };

  // PDF upload handler
  const handlePdfUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Lütfen bir PDF dosyası seçin');
      return;
    }

    // Period mode için identifier ve period kontrolü
    if (viewMode === 'period') {
      if (!selectedIdentifier) {
        setError('Lütfen önce bir identifier seçin');
        return;
      }
      if (!selectedPeriod) {
        setError('Lütfen önce bir period seçin');
        return;
      }
    }

    try {
      setUploadingPdf(true);
      setError(null);

      const formData = new FormData();
      formData.append('pdfData', file);
      
      // Period mode için identifier ve period bilgilerini ekle
      if (viewMode === 'period' && selectedIdentifier && selectedPeriod) {
        const period = periods.find(p => p.value === selectedPeriod);
        if (period) {
          formData.append('identifier', selectedIdentifier);
          formData.append('periodStart', period.start);
          formData.append('periodEnd', period.end);
        }
      }

      const response = await edefterFetch(`${EDEFTER_API_URL}/readVergiPDF`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setPdfData(data);
        
        // Bilanço verisi kontrolü
        const hasBilanco = data.bilanco && data.bilanco.length > 0;
        const hasGelirTablosu = data.gelir_tablosu && data.gelir_tablosu.length > 0;
        
        if (!hasBilanco && hasGelirTablosu) {
          console.warn('[PDF Upload] ⚠️ PDF\'te bilanço verisi bulunamadı, sadece gelir tablosu var. Bilanço kalemleri (ör. 329) eşleşmeyecek.');
          setError('Uyarı: PDF\'te bilanço verisi bulunamadı. Sadece gelir tablosu okundu. Bilanço kalemleri için Declaration Balance değerleri görünmeyecek.');
        } else if (!hasBilanco && !hasGelirTablosu) {
          setError('PDF\'te ne bilanço ne de gelir tablosu bulunamadı. PDF formatını kontrol edin.');
        }
        
        // Önce yüklenen dosyayı göster (kullanıcı hemen görebilsin)
        const fileUrl = URL.createObjectURL(file);
        setPdfFile(fileUrl);
        
        // PDF başarıyla kaydedildiyse, kaydedilmiş PDF'i yükle (arka planda)
        if (viewMode === 'period' && selectedIdentifier && selectedPeriod) {
          // Kısa bir gecikme ekle (PDF'in kaydedilmesi için)
          setTimeout(async () => {
            await loadSavedPdf();
          }, 1000);
        }
        
        // PDF yüklendikten sonra PDF tab'ına geçme - kullanıcı manuel olarak tıklayabilir
        // setActiveTab(1);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'PDF okuma hatası');
      }
    } catch (err) {
      console.error('Error uploading PDF:', err);
      setError('PDF yüklenirken bir hata oluştu: ' + err.message);
    } finally {
      setUploadingPdf(false);
    }
  };

  // Kaydedilmiş PDF'i yükleme
  const loadSavedPdf = async () => {
    if (viewMode !== 'period' || !selectedIdentifier || !selectedPeriod) {
      return;
    }

    try {
      const period = periods.find(p => p.value === selectedPeriod);
      if (!period) return;

      const response = await edefterFetch(
        `${EDEFTER_API_URL}/getPdf?identifier=${encodeURIComponent(selectedIdentifier)}&periodStart=${encodeURIComponent(period.start)}&periodEnd=${encodeURIComponent(period.end)}`
      );

      if (response.ok) {
        const blob = await response.blob();
        const fileUrl = URL.createObjectURL(blob);
        // Eski URL'i temizle
        if (pdfFile) {
          URL.revokeObjectURL(pdfFile);
        }
        setPdfFile(fileUrl);
      } else {
        // PDF bulunamadıysa, mevcut PDF'i koru (yüklenen dosyayı göster)
        console.log('Saved PDF not found, keeping uploaded file');
      }
    } catch (err) {
      console.error('Error loading saved PDF:', err);
      // Hata durumunda mevcut PDF'i koru
    }
  };

  // PDF'ten okunan değeri account code'a göre bul
  const getPdfValueForAccount = (accountCode) => {
    if (!pdfData || !accountCode) {
      console.log('[PDF Match] No pdfData or accountCode:', { pdfData: !!pdfData, accountCode });
      return null;
    }

    // İlk çağrıda PDF verisinin tamamını logla (sadece bir kez)
    if (!getPdfValueForAccount._logged) {
      console.log('[PDF Match] 📄 PDF Verisi Özeti:', {
        hasBilanco: !!(pdfData.bilanco && pdfData.bilanco.length > 0),
        hasGelirTablosu: !!(pdfData.gelir_tablosu && pdfData.gelir_tablosu.length > 0),
        bilancoDonemler: pdfData.bilanco ? pdfData.bilanco.map(d => ({
          donem: d.donem,
          yil: d.yil,
          kalemSayisi: d.kalemler?.length || 0
        })) : [],
        gelirTablosuDonemler: pdfData.gelir_tablosu ? pdfData.gelir_tablosu.map(d => ({
          donem: d.donem,
          yil: d.yil,
          kalemSayisi: d.kalemler?.length || 0
        })) : []
      });
      
      // Bilanço kodlarını listele
      if (pdfData.bilanco && pdfData.bilanco.length > 0) {
        const cariDonem = pdfData.bilanco.find(d => d.donem === 'cari');
        if (cariDonem && cariDonem.kalemler) {
          const bilancoKodlar = cariDonem.kalemler
            .map(k => ({ kod: k.kod, baslik: k.baslik }))
            .filter(k => k.kod)
            .sort((a, b) => (a.kod || '').localeCompare(b.kod || ''));
          console.log('[PDF Match] 📋 Bilanço - Tüm Kodlar (' + bilancoKodlar.length + ' adet):', bilancoKodlar);
        }
      }
      
      // Gelir tablosu kodlarını listele
      if (pdfData.gelir_tablosu && pdfData.gelir_tablosu.length > 0) {
        const cariDonem = pdfData.gelir_tablosu.find(d => d.donem === 'cari');
        if (cariDonem && cariDonem.kalemler) {
          const gelirKodlar = cariDonem.kalemler
            .map(k => ({ kod: k.kod, baslik: k.baslik }))
            .filter(k => k.kod)
            .sort((a, b) => (a.kod || '').localeCompare(b.kod || ''));
          console.log('[PDF Match] 📊 Gelir Tablosu - Tüm Kodlar (' + gelirKodlar.length + ' adet):', gelirKodlar);
        }
      }
      
      getPdfValueForAccount._logged = true;
    }

    // Account code'u normalize et (trim, lowercase, "kod" önekini kaldır)
    const normalizeCode = (code) => {
      if (!code) return '';
      return String(code).trim().toLowerCase().replace(/^kod/, '');
    };

    const normalizedAccountCode = normalizeCode(accountCode);
    console.log('[PDF Match] Looking for account code:', {
      original: accountCode,
      normalized: normalizedAccountCode
    });

    // Bilanço verilerini kontrol et
    if (pdfData.bilanco && pdfData.bilanco.length > 0) {
      console.log('[PDF Match] ✅ Bilanço verisi mevcut, toplam dönem sayısı:', pdfData.bilanco.length);
      // Cari dönem verilerini kullan
      const cariDonem = pdfData.bilanco.find(d => d.donem === 'cari');
      if (cariDonem && cariDonem.kalemler) {
        console.log('[PDF Match] Bilanço - Total kalemler:', cariDonem.kalemler.length);
        console.log('[PDF Match] Bilanço - Sample kodlar:', cariDonem.kalemler.slice(0, 5).map(k => ({
          kod: k.kod,
          baslik: k.baslik,
          harcama: k.harcama,
          tutar: k.tutar
        })));
        // 329 kodunu özellikle ara
        const kod329 = cariDonem.kalemler.find(k => {
          const normalized = normalizeCode(k.kod);
          return normalized === '329' || normalized === 'kod329';
        });
        if (kod329) {
          console.log('[PDF Match] 🔍 Bilanço - 329 kodu bulundu:', kod329);
        } else {
          console.log('[PDF Match] 🔍 Bilanço - 329 kodu arandı ama bulunamadı');
          const allBilancoKodlar = cariDonem.kalemler.map(k => ({
            kod: k.kod,
            normalized: normalizeCode(k.kod),
            baslik: k.baslik
          })).filter(k => k.kod);
          console.log('[PDF Match] Bilanço - Tüm kodlar:', allBilancoKodlar.map(k => k.kod).slice(0, 30));
        }

        const kalem = cariDonem.kalemler.find(k => {
          if (!k.kod) return false;
          const normalizedPdfKod = normalizeCode(k.kod);
          
          // Tam eşleşme kontrolü
          if (normalizedPdfKod === normalizedAccountCode) {
            console.log('[PDF Match] ✅ Bilanço - Tam eşleşme bulundu:', {
              accountCode: accountCode,
              pdfKod: k.kod,
              normalizedAccountCode,
              normalizedPdfKod,
              baslik: k.baslik,
              deger: k.harcama || k.tutar
            });
            return true;
          }
          
          // Sayısal kodlar için leading zero'ları yok say
          const pdfNum = normalizedPdfKod.replace(/^0+/, '') || '0';
          const accountNum = normalizedAccountCode.replace(/^0+/, '') || '0';
          if (pdfNum === accountNum) {
            console.log('[PDF Match] ✅ Bilanço - Sayısal eşleşme bulundu:', {
              accountCode: accountCode,
              pdfKod: k.kod,
              normalizedAccountCode,
              normalizedPdfKod,
              accountNum,
              pdfNum,
              baslik: k.baslik,
              deger: k.harcama || k.tutar
            });
            return true;
          }
          
          return false;
        });
        
        if (kalem) {
          const deger = kalem.harcama || kalem.tutar || 0;
          console.log('[PDF Match] ✅ Bilanço - Değer döndürülüyor:', deger);
          return deger;
        } else {
          const allKodlar = cariDonem.kalemler.map(k => ({
            kod: k.kod,
            normalized: normalizeCode(k.kod),
            baslik: k.baslik
          })).filter(k => k.kod);
          console.log('[PDF Match] ❌ Bilanço - Eşleşme bulunamadı:', {
            accountCode: accountCode,
            normalizedAccountCode,
            availableKodlar: allKodlar.map(k => k.kod).slice(0, 20),
            allKodlarWithDetails: allKodlar.slice(0, 20)
          });
        }
      }
    }

    // Gelir tablosu verilerini kontrol et
    if (pdfData.gelir_tablosu && pdfData.gelir_tablosu.length > 0) {
      const cariDonem = pdfData.gelir_tablosu.find(d => d.donem === 'cari');
      if (cariDonem && cariDonem.kalemler) {
        console.log('[PDF Match] Gelir Tablosu - Total kalemler:', cariDonem.kalemler.length);
        console.log('[PDF Match] Gelir Tablosu - Sample kodlar:', cariDonem.kalemler.slice(0, 5).map(k => ({
          kod: k.kod,
          baslik: k.baslik,
          tutar: k.tutar,
          harcama: k.harcama
        })));

        const kalem = cariDonem.kalemler.find(k => {
          if (!k.kod) return false;
          const normalizedPdfKod = normalizeCode(k.kod);
          
          // Tam eşleşme kontrolü
          if (normalizedPdfKod === normalizedAccountCode) {
            console.log('[PDF Match] ✅ Gelir Tablosu - Tam eşleşme bulundu:', {
              accountCode: accountCode,
              pdfKod: k.kod,
              normalizedAccountCode,
              normalizedPdfKod,
              baslik: k.baslik,
              deger: k.tutar || k.harcama
            });
            return true;
          }
          
          // Sayısal kodlar için leading zero'ları yok say
          const pdfNum = normalizedPdfKod.replace(/^0+/, '') || '0';
          const accountNum = normalizedAccountCode.replace(/^0+/, '') || '0';
          if (pdfNum === accountNum) {
            console.log('[PDF Match] ✅ Gelir Tablosu - Sayısal eşleşme bulundu:', {
              accountCode: accountCode,
              pdfKod: k.kod,
              normalizedAccountCode,
              normalizedPdfKod,
              accountNum,
              pdfNum,
              baslik: k.baslik,
              deger: k.tutar || k.harcama
            });
            return true;
          }
          
          return false;
        });
        
        if (kalem) {
          const deger = kalem.tutar || kalem.harcama || 0;
          console.log('[PDF Match] ✅ Gelir Tablosu - Değer döndürülüyor:', deger);
          return deger;
        } else {
          const allKodlar = cariDonem.kalemler.map(k => ({
            kod: k.kod,
            normalized: normalizeCode(k.kod),
            baslik: k.baslik
          })).filter(k => k.kod);
          console.log('[PDF Match] ❌ Gelir Tablosu - Eşleşme bulunamadı:', {
            accountCode: accountCode,
            normalizedAccountCode,
            availableKodlar: allKodlar.map(k => k.kod).slice(0, 20),
            allKodlarWithDetails: allKodlar.slice(0, 20)
          });
        }
      }
    }

    console.log('[PDF Match] ❌ Hiçbir eşleşme bulunamadı:', {
      accountCode: accountCode,
      normalizedAccountCode,
      hasBilanco: !!(pdfData.bilanco && pdfData.bilanco.length > 0),
      hasGelirTablosu: !!(pdfData.gelir_tablosu && pdfData.gelir_tablosu.length > 0),
      bilancoDonemler: pdfData.bilanco ? pdfData.bilanco.map(d => ({ donem: d.donem, kalemSayisi: d.kalemler?.length || 0 })) : null,
      gelirTablosuDonemler: pdfData.gelir_tablosu ? pdfData.gelir_tablosu.map(d => ({ donem: d.donem, kalemSayisi: d.kalemler?.length || 0 })) : null
    });
    return null;
  };

  // PDF değerinin debit veya credit balance ile eşleşip eşleşmediğini kontrol et
  const isPdfValueMatching = (pdfValue, debitBalance, creditBalance) => {
    if (pdfValue === null || pdfValue === undefined) return false;
    const pdfVal = Math.abs(parseFloat(pdfValue) || 0); // PDF değeri mutlak değer olarak alınır
    const debitVal = Math.abs(parseFloat(debitBalance) || 0);
    const creditVal = Math.abs(parseFloat(creditBalance) || 0);
    
    // Hangi sütunun dolu olduğuna bakarak karşılaştırma yap
    // Eğer debit balance doluysa onunla, credit balance doluysa onunla karşılaştır
    if (debitVal > 0 && creditVal === 0) {
      // Sadece debit balance var, onunla karşılaştır
      return Math.abs(pdfVal - debitVal) < 0.01;
    } else if (creditVal > 0 && debitVal === 0) {
      // Sadece credit balance var, onunla karşılaştır
      return Math.abs(pdfVal - creditVal) < 0.01;
    } else if (debitVal > 0 && creditVal > 0) {
      // Her ikisi de dolu (bu durum normalde olmamalı ama yine de kontrol et)
      return Math.abs(pdfVal - debitVal) < 0.01 || Math.abs(pdfVal - creditVal) < 0.01;
    } else {
      // Her ikisi de boş
      return pdfVal === 0;
    }
  };

  // ViewMode değiştiğinde expanded rows'u temizle
  useEffect(() => {
    setExpandedRows(new Set());
    // Single mode'a geçildiğinde tab'ı Table'a resetle
    if (viewMode === 'single') {
      setActiveTab(0);
    }
  }, [viewMode]);

  // Cleanup PDF URL on unmount
  useEffect(() => {
    return () => {
      if (pdfFile) {
        URL.revokeObjectURL(pdfFile);
      }
    };
  }, [pdfFile]);

  return (
    <Box sx={{ minHeight: '100vh', py: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Header Banner */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            p: 3,
            color: 'white'
          }}>
            {companyInfo ? (
              <Box>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Grid item>
                    <Avatar sx={{ 
                      width: 64, 
                      height: 64,
                      background: 'rgba(255,255,255,0.25)',
                      border: '3px solid rgba(255,255,255,0.3)'
                    }}>
                      <Business sx={{ fontSize: '1.5rem' }} />
                    </Avatar>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {companyInfo.title || 'Company Information'}
                    </Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.2)' }} />
                <Grid container spacing={3}>
                  {companyInfo.taxNumber && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AccountBalance sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Tax ID Number
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {companyInfo.taxNumber}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {companyInfo.taxOffice && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AccountBalance sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Tax Office
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {companyInfo.taxOffice}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {companyInfo.email && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Email sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Email
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {companyInfo.email}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {companyInfo.phone && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Phone sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Phone
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {companyInfo.phone}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {companyInfo.address && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LocationOn sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Address
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {companyInfo.address}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ) : (
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Avatar sx={{ 
                    width: 64, 
                    height: 64,
                    background: 'rgba(255,255,255,0.25)',
                    border: '3px solid rgba(255,255,255,0.3)'
                  }}>
                    <AttachMoney sx={{ fontSize: '1.5rem' }} />
                  </Avatar>
                </Grid>
                <Grid item xs>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Trial Balance
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Search and view trial balance reports by company and period
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4 }}>
            
            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* View Mode Selection */}
            <Box sx={{ mb: 3 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                  View Mode
                </FormLabel>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  aria-label="view mode"
                  size="small"
                >
                  <ToggleButton value="single" aria-label="single month">
                    Single Month
                  </ToggleButton>
                  <ToggleButton value="period" aria-label="cumulative period">
                    Cumulative Period
                  </ToggleButton>
                </ToggleButtonGroup>
              </FormControl>
            </Box>

            {/* Search Section */}
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                {/* Identifier Select */}
                <Grid item xs={12} sm={viewMode === 'period' ? 12 : 6}>
                  <Autocomplete
                    freeSolo
                    options={identifiers}
                    value={selectedIdentifier}
                    inputValue={identifierInputValue}
                    onChange={handleIdentifierChange}
                    onInputChange={(event, newInputValue) => {
                      setIdentifierInputValue(newInputValue);
                    }}
                    loading={loading}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      return option || '';
                    }}
                    isOptionEqualToValue={(option, value) => option === value}
                    filterOptions={(options, params) => {
                      const filtered = options.filter((option) =>
                        option.toLowerCase().includes(params.inputValue.toLowerCase())
                      );
                      return filtered;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Company Tax Number"
                        variant="outlined"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <Business color={selectedIdentifier ? 'primary' : 'disabled'} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <>
                              {loading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    sx={{
                      width: '100%',
                      display: 'block',
                      '& .MuiAutocomplete-root': {
                        width: '100%'
                      },
                      '& .MuiAutocomplete-inputRoot': {
                        width: '100% !important',
                        minWidth: '100% !important',
                        flexWrap: 'nowrap'
                      },
                      '& .MuiAutocomplete-input': {
                        width: '100% !important',
                        minWidth: 0,
                        flex: '1 1 auto'
                      }
                    }}
                  />
                </Grid>

                {/* Document Select (Single Month Mode) */}
                {viewMode === 'single' && (
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                        options={documents}
                        value={selectedDocument}
                        onChange={handleDocumentChange}
                        disabled={!selectedIdentifier || loadingDocuments}
                        loading={loadingDocuments}
                        getOptionLabel={(option) => getDocumentLabel(option)}
                        isOptionEqualToValue={(option, value) => {
                          if (!option || !value) return false;
                          // Compare by id if available, otherwise by uniqueId + period
                          if (option.id && value.id) return option.id === value.id;
                          return option.uniqueId === value.uniqueId && 
                                 option.periodCoveredStart === value.periodCoveredStart &&
                                 option.periodCoveredEnd === value.periodCoveredEnd;
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="General Journal Period"
                            variant="outlined"
                            fullWidth
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CalendarToday color={selectedDocument ? 'primary' : 'disabled'} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <>
                                  {loadingDocuments ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        sx={{
                          width: '100%',
                          display: 'block',
                          '& .MuiAutocomplete-root': {
                            width: '100%'
                          },
                          '& .MuiAutocomplete-inputRoot': {
                            width: '100% !important',
                            minWidth: '100% !important',
                            flexWrap: 'nowrap'
                          },
                          '& .MuiAutocomplete-input': {
                            width: '100% !important',
                            minWidth: 0,
                            flex: '1 1 auto'
                          }
                        }}
                      />
                  </Grid>
                )}

                {/* Period Select (Cumulative Period Mode) */}
                {viewMode === 'period' && (
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={periods}
                      value={periods.find(p => p.value === selectedPeriod) || null}
                      onChange={handlePeriodChange}
                      disabled={!selectedIdentifier}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Period"
                          variant="outlined"
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarToday color={selectedPeriod ? 'primary' : 'disabled'} />
                              </InputAdornment>
                            ),
                            endAdornment: params.InputProps.endAdornment,
                          }}
                        />
                      )}
                      sx={{
                        width: '100%',
                        display: 'block',
                        '& .MuiAutocomplete-root': {
                          width: '100%'
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          width: '100% !important',
                          minWidth: '100% !important',
                          flexWrap: 'nowrap'
                        },
                        '& .MuiAutocomplete-input': {
                          width: '100% !important',
                          minWidth: 0,
                          flex: '1 1 auto'
                        }
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Mizan Table and PDF Viewer with Tabs */}
            {mizanData && mizanData.rows && mizanData.rows.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Description sx={{ color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      Trial Balance
                    </Typography>
                    {mizanData.periodCoveredStart && mizanData.periodCoveredEnd && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                        <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(mizanData.periodCoveredStart)} - {formatDate(mizanData.periodCoveredEnd)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {viewMode === 'period' && (
                      <>
                        <input
                          accept="application/pdf"
                          style={{ display: 'none' }}
                          id="pdf-upload-button"
                          type="file"
                          onChange={handlePdfUpload}
                          disabled={uploadingPdf}
                        />
                        <label htmlFor="pdf-upload-button">
                          <Button
                            variant="outlined"
                            color="secondary"
                            component="span"
                            startIcon={uploadingPdf ? <CircularProgress size={20} /> : <PictureAsPdf />}
                            disabled={uploadingPdf}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                            }}
                          >
                            {uploadingPdf ? 'Yükleniyor...' : 'PDF Yükle'}
                          </Button>
                        </label>
                      </>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Download />}
                      onClick={handleDownloadExcel}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      Download Excel
                    </Button>
                  </Box>
                </Box>

                <Card elevation={8} sx={{ borderRadius: 3 }}>
                  {viewMode === 'period' && (
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} aria-label="trial balance tabs">
                        <Tab label="Table" icon={<Description />} iconPosition="start" />
                        <Tab label="PDF" icon={<PictureAsPdf />} iconPosition="start" disabled={!pdfFile} />
                      </Tabs>
                    </Box>
                  )}

                  {(viewMode === 'single' || activeTab === 0) && (
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap', width: 50 }}>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Account Code
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Sub Code
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Account Name
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Account Detail Name
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Debit
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Credit
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Debit Balance
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Credit Balance
                          </TableCell>
                          {viewMode === 'period' && (
                            <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                              Declaration Balance
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {groupedMizanData ? (
                          // Gruplanmış görünüm (hem single hem period mode)
                          groupedMizanData.map((group, groupIndex) => {
                            const isExpanded = expandedRows.has(group.accountCode);
                            const hasMultipleDetails = group.hasMultipleDetails;
                            const hasAnyDetail = group.subRows.length > 0;

                            const renderSimpleRow = (rowData) => (
                              <TableRow
                                key={`group-simple-${group.accountCode}-${groupIndex}`}
                                hover
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                  },
                                  borderLeft: '4px solid transparent',
                                }}
                              >
                                <TableCell sx={{ width: 50 }} />
                                <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                                  {rowData.hesapKodu || group.accountCode}
                                </TableCell>
                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                  {rowData.subId || ''}
                                </TableCell>
                                <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                                  {rowData.mainDescription || group.mainDescription}
                                </TableCell>
                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                  {rowData.subDescription || ''}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                  {formatNumber(rowData.borc ?? group.totalBorc)}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                  {formatNumber(rowData.alacak ?? group.totalAlacak)}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                  {formatNumber(rowData.borcBakiyesi ?? group.totalBorcBakiyesi)}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                  {formatNumber(rowData.alacakBakiyesi ?? group.totalAlacakBakiyesi)}
                                </TableCell>
                                {viewMode === 'period' && (
                                  <TableCell sx={{ 
                                    textAlign: 'right', 
                                    fontWeight: 600, 
                                    fontSize: '0.8rem',
                                    color: (() => {
                                      const pdfValue = getPdfValueForAccount(rowData.hesapKodu || group.accountCode);
                                      const debitBalance = rowData.borcBakiyesi ?? group.totalBorcBakiyesi;
                                      const creditBalance = rowData.alacakBakiyesi ?? group.totalAlacakBakiyesi;
                                      const debitVal = Math.abs(parseFloat(debitBalance) || 0);
                                      const creditVal = Math.abs(parseFloat(creditBalance) || 0);
                                      
                                      if (pdfValue === null || pdfValue === undefined) {
                                        return 'text.primary';
                                      }
                                      
                                      const pdfVal = Math.abs(parseFloat(pdfValue) || 0);
                                      
                                      // Eğer debit veya credit balance dolu ama PDF değeri 0 ise kırmızı
                                      if (pdfVal === 0 && (debitVal > 0 || creditVal > 0)) {
                                        return 'error.main';
                                      }
                                      
                                      // PDF değeri 0 ise ve balance'lar da 0 ise normal renk
                                      if (pdfVal === 0) {
                                        return 'text.primary';
                                      }
                                      
                                      // PDF değeri var, eşleşme kontrolü yap
                                      return isPdfValueMatching(
                                        pdfValue,
                                        debitBalance,
                                        creditBalance
                                      ) ? 'success.main' : 'error.main';
                                    })()
                                  }}>
                                    {formatNumber(getPdfValueForAccount(rowData.hesapKodu || group.accountCode))}
                                  </TableCell>
                                )}
                              </TableRow>
                            );

                            if (!hasAnyDetail || (hasAnyDetail && !hasMultipleDetails)) {
                              const rowToRender = group.subRows[0] || group.baseRow || {
                                hesapKodu: group.accountCode,
                                subId: '',
                                mainDescription: group.mainDescription,
                                subDescription: '',
                                borc: group.totalBorc,
                                alacak: group.totalAlacak,
                                borcBakiyesi: group.totalBorcBakiyesi,
                                alacakBakiyesi: group.totalAlacakBakiyesi,
                              };
                              return renderSimpleRow(rowToRender);
                            }

                            return (
                              <React.Fragment key={`group-${group.accountCode}-${groupIndex}`}>
                                {/* Ana satır (toplam) */}
                                <TableRow 
                                  hover
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                    },
                                    borderLeft: '4px solid transparent',
                                    backgroundColor: 'rgba(25, 118, 210, 0.02)'
                                  }}
                                >
                                  <TableCell sx={{ width: 50 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleToggleRow(group.accountCode)}
                                      sx={{ p: 0.5 }}
                                    >
                                      {isExpanded ? (
                                        <ExpandMore sx={{ fontSize: 20 }} />
                                      ) : (
                                        <ChevronRight sx={{ fontSize: 20 }} />
                                      )}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                                    {group.accountCode}
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                    {/* Top-level satırda sub code gösterme */}
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem', fontWeight: 600 }}>
                                    {group.mainDescription}
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                    Total
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                    {formatNumber(group.totalBorc)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                    {formatNumber(group.totalAlacak)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                    {formatNumber(group.totalBorcBakiyesi)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                    {formatNumber(group.totalAlacakBakiyesi)}
                                  </TableCell>
                                  {viewMode === 'period' && (
                                    <TableCell sx={{ 
                                      textAlign: 'right', 
                                      fontWeight: 600, 
                                      fontSize: '0.8rem',
                                      color: (() => {
                                        const pdfValue = getPdfValueForAccount(group.accountCode);
                                        const debitBalance = group.totalBorcBakiyesi;
                                        const creditBalance = group.totalAlacakBakiyesi;
                                        const debitVal = Math.abs(parseFloat(debitBalance) || 0);
                                        const creditVal = Math.abs(parseFloat(creditBalance) || 0);
                                        
                                        if (pdfValue === null || pdfValue === undefined) {
                                          return 'text.primary';
                                        }
                                        
                                        const pdfVal = Math.abs(parseFloat(pdfValue) || 0);
                                        
                                        // Eğer debit veya credit balance dolu ama PDF değeri 0 ise kırmızı
                                        if (pdfVal === 0 && (debitVal > 0 || creditVal > 0)) {
                                          return 'error.main';
                                        }
                                        
                                        // PDF değeri 0 ise ve balance'lar da 0 ise normal renk
                                        if (pdfVal === 0) {
                                          return 'text.primary';
                                        }
                                        
                                        // PDF değeri var, eşleşme kontrolü yap
                                        return isPdfValueMatching(
                                          pdfValue,
                                          debitBalance,
                                          creditBalance
                                        ) ? 'success.main' : 'error.main';
                                      })()
                                    }}>
                                      {formatNumber(getPdfValueForAccount(group.accountCode))}
                                    </TableCell>
                                  )}
                                </TableRow>
                                
                                {/* Detay satırları (Collapse içinde) */}
                                <TableRow>
                                  <TableCell colSpan={viewMode === 'period' ? 10 : 9} sx={{ py: 0, border: 'none' }}>
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ margin: 0 }}>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow sx={{ backgroundColor: 'rgba(25, 118, 210, 0.05)' }}>
                                              <TableCell sx={{ width: 50 }} />
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                Account Code
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                Sub Code
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                Account Name
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                Account Detail Name
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                Debit
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                Credit
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                Debit Balance
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                Credit Balance
                                              </TableCell>
                                              {viewMode === 'period' && (
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                  Declaration Balance
                                                </TableCell>
                                              )}
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {group.subRows.map((row, rowIndex) => (
                                              <TableRow 
                                                key={`detail-${group.accountCode}-${row.subId}-${rowIndex}`}
                                                sx={{
                                                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                  '&:hover': {
                                                    backgroundColor: 'rgba(25, 118, 210, 0.06)',
                                                  },
                                                }}
                                              >
                                                <TableCell sx={{ width: 50, pl: 4 }}></TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                                                  {row.hesapKodu || ''}
                                                </TableCell>
                                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                                  {row.subId || ''}
                                                </TableCell>
                                                <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                                                  {row.mainDescription || ''}
                                                </TableCell>
                                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                                  {row.subDescription || ''}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                                  {formatNumber(row.borc)}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                                  {formatNumber(row.alacak)}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                                  {formatNumber(row.borcBakiyesi)}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                                  {formatNumber(row.alacakBakiyesi)}
                                                </TableCell>
                                                {viewMode === 'period' && (
                                                  <TableCell sx={{ 
                                                    textAlign: 'right', 
                                                    fontWeight: 600, 
                                                    fontSize: '0.8rem',
                                                    color: (() => {
                                                      // Subcode'lar için renklendirme yok
                                                      if (row.subId) {
                                                        return 'text.secondary';
                                                      }
                                                      
                                                      const pdfValue = getPdfValueForAccount(row.hesapKodu);
                                                      const debitBalance = row.borcBakiyesi;
                                                      const creditBalance = row.alacakBakiyesi;
                                                      const debitVal = Math.abs(parseFloat(debitBalance) || 0);
                                                      const creditVal = Math.abs(parseFloat(creditBalance) || 0);
                                                      
                                                      if (pdfValue === null || pdfValue === undefined) {
                                                        return 'text.primary';
                                                      }
                                                      
                                                      const pdfVal = Math.abs(parseFloat(pdfValue) || 0);
                                                      
                                                      // Eğer debit veya credit balance dolu ama PDF değeri 0 ise kırmızı
                                                      if (pdfVal === 0 && (debitVal > 0 || creditVal > 0)) {
                                                        return 'error.main';
                                                      }
                                                      
                                                      // PDF değeri 0 ise ve balance'lar da 0 ise normal renk
                                                      if (pdfVal === 0) {
                                                        return 'text.primary';
                                                      }
                                                      
                                                      // PDF değeri var, eşleşme kontrolü yap
                                                      return isPdfValueMatching(
                                                        pdfValue,
                                                        debitBalance,
                                                        creditBalance
                                                      ) ? 'success.main' : 'error.main';
                                                    })()
                                                  }}>
                                                    {/* Subcode'lar için PDF'te ayrı değer yok, boş bırakılıyor */}
                                                    {row.subId ? '' : formatNumber(getPdfValueForAccount(row.hesapKodu))}
                                                  </TableCell>
                                                )}
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })
                        ) : (
                          // Veri yoksa boş durum
                          <TableRow>
                            <TableCell colSpan={viewMode === 'period' ? 10 : 9} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                {loading ? 'Yükleniyor...' : 'Veri bulunamadı'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  )}

                  {viewMode === 'period' && activeTab === 1 && pdfFile && (
                    <Box sx={{ width: '100%', height: '80vh', p: 2 }}>
                      <iframe
                        src={pdfFile}
                        width="100%"
                        height="100%"
                        style={{ border: 'none', borderRadius: '8px' }}
                        title="PDF Viewer"
                      />
                    </Box>
                  )}

                  {viewMode === 'period' && activeTab === 1 && !pdfFile && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <PictureAsPdf sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                      <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                        No PDF Uploaded
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please upload a PDF file to view it here.
                      </Typography>
                    </Box>
                  )}
                </Card>
              </Box>
            )}

            {/* Empty State */}
            {!mizanData && !loading && !selectedDocument && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Box sx={{ display: 'inline-flex', p: 3, borderRadius: 3, backgroundColor: 'grey.100', mb: 2 }}>
                  <AttachMoney sx={{ fontSize: 48, color: 'grey.400' }} />
                </Box>
                <Typography variant="h6" sx={{ mb: 1, color: 'text.primary', fontSize: '1rem' }}>
                  No Trial Balance Selected
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Select a company tax number and ledger period to view the trial balance.
                </Typography>
              </Box>
            )}

            {/* Loading State */}
            {loading && selectedDocument && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CircularProgress size={40} sx={{ color: '#1976d2' }} />
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
                  Loading trial balance...
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default TrialBalance;

